import { documentBody } from '@markbird/shared';
import { maskCode } from './wikilinks';

/**
 * Inline `#tags` written in the body.
 *
 * Frontmatter is the tidy place for tags, but nobody stops mid-sentence to go
 * edit a metadata block — tagging inline is how notes actually get tagged. Both
 * feed the same tag list.
 *
 * The hard part is *not* matching: a markdown heading, a colour, a URL
 * fragment and a C# reference all start with `#`. Each of those is excluded
 * deliberately below; when in doubt the rule is to miss a tag rather than to
 * invent one, because a false tag pollutes every tag list in the app.
 */

/**
 * A tag is `#` followed by at least one letter, then letters, digits, `-`, `_`
 * or `/` for nesting. Digits alone are excluded so `#1` stays an issue
 * reference and `#fff` stays... well, a tag — but `#123` does not.
 */
const TAG_RE = /(^|[\s([{'"])#([\p{L}][\p{L}\p{N}_/-]*)/gu;

/** Tags written inline in a document's body, in order, de-duplicated. */
export function inlineTags(content: string): string[] {
  // Frontmatter has its own `tags:` key, and code is not prose.
  const text = maskCode(documentBody(content));
  const out: string[] = [];
  const seen = new Set<string>();
  const re = new RegExp(TAG_RE.source, TAG_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const raw = m[2];
    if (!raw) continue;
    // A trailing slash is a typo, not an empty child tag.
    const tag = raw.replace(/\/+$/, '');
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

/** Every ancestor of a nested tag, so `a/b/c` also counts under `a` and `a/b`. */
export function tagAncestors(tag: string): string[] {
  const parts = tag.split('/').filter(Boolean);
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'));
}

/**
 * All distinct tags across a set of tag lists, expanded to include ancestors
 * and sorted. This is what a tag pane shows: `work/urgent` implies `work`.
 */
export function tagTree(lists: string[][]): string[] {
  const map = new Map<string, string>();
  for (const list of lists) {
    for (const tag of list) {
      for (const node of tagAncestors(tag)) {
        const key = node.toLowerCase();
        if (!map.has(key)) map.set(key, node);
      }
    }
  }
  return [...map.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
